import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { PageHeader } from '@/components/PageHeader';
import {
  TestConnectionForm,
  ProgressAnimation,
  AccountsResultsCard,
  SubscriptionModal,
} from '@/components/collector';
import { KPICards } from '@/components/collector/KPICards';
import { useTestPortalBot, useJobProgress, useCancelJob } from '@/hooks/usePortalBot';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface Account {
  serialNo: number;
  accountNumber: string;
  accountAssociated: string;
  latestBillUrl: string | null;
  has12Months: boolean;
}

export default function CollectorSandbox() {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [lastTested, setLastTested] = useState<Date | undefined>(undefined);
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>(undefined);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [testStatus, setTestStatus] = useState<'Success' | 'Failed' | 'Processing'>('Success');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [rateLimitUntil, setRateLimitUntil] = useState<Date | null>(null);
  const [simulatedProgress, setSimulatedProgress] = useState(0);
  const [testCredentials, setTestCredentials] = useState<{
    username: string;
    supplierName: string;
  } | null>(null);

  const testPortalBot = useTestPortalBot();
  const { data: jobProgress } = useJobProgress(jobId);
  const cancelJob = useCancelJob();

  const isTesting = jobId && (jobProgress?.status === 'processing' || testPortalBot.isPending);

  // Authentication guard with continuous listener
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
      
      if (!session) {
        toast({
          title: 'Authentication required',
          description: 'Please log in to use the Collector Sandbox',
          variant: 'destructive',
        });
      }
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      
      if (event === 'SIGNED_OUT') {
        setJobId(null);
        setAccounts([]);
        toast({
          title: 'Session ended',
          description: 'Please log in again to continue',
          variant: 'destructive',
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  useEffect(() => {
    if (jobProgress?.status === 'completed' && jobProgress.result) {
      const result = jobProgress.result;
      
      if (result.accounts && Array.isArray(result.accounts)) {
        const mappedAccounts: Account[] = result.accounts.map((acc: any, index: number) => ({
          serialNo: index + 1,
          accountNumber: acc.accountNumber || 'N/A',
          accountAssociated: acc.accountAssociated || 'N/A',
          latestBillUrl: acc.latestBillUrl || null,
          has12Months: acc.has12Months || false,
        }));

        setAccounts(mappedAccounts);
        setLastTested(new Date());
        setJobId(null);

        toast({
          title: 'Connection successful',
          description: `Found ${mappedAccounts.length} account${mappedAccounts.length !== 1 ? 's' : ''}`,
        });
      }
    } else if (jobProgress?.status === 'failed') {
      setJobId(null);
      toast({
        title: 'Connection failed',
        description: jobProgress.error_message || 'Connection test failed',
        variant: 'destructive',
      });
    } else if (jobProgress?.status === 'canceled') {
      setJobId(null);
    }
  }, [jobProgress, toast]);

  const handleTestConnection = (data: {
    supplierId: string;
    supplierName: string;
    username: string;
    password: string;
  }) => {
    // Check rate limiting
    if (rateLimitUntil && new Date() < rateLimitUntil) {
      const remainingMinutes = Math.ceil((rateLimitUntil.getTime() - new Date().getTime()) / (1000 * 60));
      toast({
        title: 'Rate limit exceeded',
        description: `Please wait ${remainingMinutes} more minutes before trying again.`,
        variant: 'destructive',
      });
      return;
    }

    // Store test credentials for display
    setTestCredentials({
      username: data.username,
      supplierName: data.supplierName,
    });

    // Start animation immediately
    const tempJobId = `temp-${Date.now()}`;
    setJobId(tempJobId);
    setTestStatus('Processing');
    
    // Simulate progress for better UX during long waits
    setSimulatedProgress(0);
    const progressInterval = setInterval(() => {
      setSimulatedProgress(prev => {
        const increment = Math.random() * 3; // Slower, more realistic progress
        const newProgress = prev + increment;
        return newProgress > 85 ? 85 : newProgress; // Don't go to 100% until API responds
      });
    }, 1500); // Faster updates for more responsive feel

    // Safety timeout to stop animation after 2 minutes
    const safetyTimeout = setTimeout(() => {
      clearInterval(progressInterval);
      setSimulatedProgress(0);
      setJobId(null);
      setTestStatus('Failed');
      toast({
        title: 'Connection timeout',
        description: 'The portal test timed out after 2 minutes. Please try again.',
        variant: 'destructive',
      });
    }, 120000); // 2 minutes
    
    testPortalBot.mutate(data, {
      onSuccess: (response) => {
        clearInterval(progressInterval); // Clean up progress simulation
        clearTimeout(safetyTimeout); // Clean up safety timeout
        console.log('[Portal Bot] Success response:', response);
        
        // Transform the accounts data to match our interface
        const transformedAccounts: Account[] = (response.accounts || []).map((account: any, index: number) => ({
          serialNo: index + 1,
          accountNumber: account.account_number || account.accountNumber || `Account ${index + 1}`,
          accountAssociated: account.account_associated || account.accountAssociated || 'N/A',
          latestBillUrl: account.latest_bill_url || account.latestBillUrl || null,
          has12Months: account.twelve_months_available || account.has12Months || false
        }));
        
        setAccounts(transformedAccounts);
        setLastTested(new Date());
        setTestStatus(transformedAccounts.length > 0 ? 'Success' : 'Failed');
        setJobId(null); // Stop animation
        setSimulatedProgress(100); // Complete the progress
        
        // Reset failed attempts on success
        setFailedAttempts(0);
        setRateLimitUntil(null);
        
        toast({
          title: 'Connection test completed',
          description: `Found ${transformedAccounts.length} account${transformedAccounts.length !== 1 ? 's' : ''}`,
        });
      },
      onError: (error) => {
        clearInterval(progressInterval); // Clean up progress simulation
        clearTimeout(safetyTimeout); // Clean up safety timeout
        console.error('[Portal Bot] Error:', error);
        
        // Immediately stop animation and reset state
        setTestStatus('Failed');
        setJobId(null); // Stop animation
        setSimulatedProgress(0); // Reset progress
        
        // Check if it's an authentication error for faster feedback
        const isAuthError = error.message?.includes('Invalid credentials') || 
                           error.message?.includes('Access denied') ||
                           error.message?.includes('Portal not found');
        
        if (isAuthError) {
          console.log('[Portal Bot] Authentication error detected, stopping animation immediately');
        }
        
        // Handle rate limiting
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        
        if (newFailedAttempts >= 5) {
          const rateLimitTime = new Date(Date.now() + 5 * 60 * 1000); // 5 minutes from now
          setRateLimitUntil(rateLimitTime);
          
          toast({
            title: 'Rate limit exceeded',
            description: 'Too many failed attempts. Please wait 5 minutes before trying again.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Connection test failed',
            description: error.message || 'Please check your credentials and try again.',
            variant: 'destructive',
          });
        }
      }
    });
  };

  const handleCancelJob = () => {
    if (jobId) {
      // Immediately stop animation and reset state
      setJobId(null);
      setTestStatus('Failed');
      setSimulatedProgress(0);
      
      // Cancel the API call if it's still pending
      if (testPortalBot.isPending) {
        console.log('Cancelling portal bot test...');
        // The API call will be cancelled by the AbortController in usePortalBot
      }
      
      toast({
        title: 'Test cancelled',
        description: 'The portal test has been cancelled.',
      });
    }
  };

  const handleDownloadLatest = (url: string) => {
    console.log('[Download] Starting download for URL:', url);
    
    if (url) {
      try {
        // Create a temporary link element to trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `latest-bill-${Date.now()}.pdf`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: 'Download started',
          description: 'Your latest bill is being downloaded.',
        });
      } catch (error) {
        console.error('[Download] Error downloading bill:', error);
        toast({
          title: 'Download failed',
          description: 'Unable to download the bill. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const handle12MonthsClick = (account: Account) => {
    console.log('[12 Months] Account:', account.accountNumber, 'Has 12 months:', account.has12Months);
    
    if (account.has12Months) {
      // TODO: Implement 12 months download logic
      toast({
        title: '12 Months Data',
        description: `Downloading 12 months of data for account ${account.accountNumber}`,
      });
    } else {
      // Show subscription modal
      setShowSubscriptionModal(true);
    }
  };

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <ModuleAccessGuard moduleId="bill_collection">
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          </div>
        </div>
      </ModuleAccessGuard>
    );
  }

  // Show error if not authenticated
  if (!isAuthenticated) {
    return (
      <ModuleAccessGuard moduleId="bill_collection">
        <div className="container mx-auto py-6">
          <Alert variant="destructive">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Please log in to access the Collector Sandbox.
            </AlertDescription>
          </Alert>
        </div>
      </ModuleAccessGuard>
    );
  }

  return (
    <ModuleAccessGuard moduleId="bill_collection">
      <div className="container mx-auto py-6 space-y-6">
        <PageHeader
          title="Collector Sandbox"
          description="Test supplier portal credentials with real-time progress tracking"
        />

        <Alert variant="default" className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-800">
            <strong>🚀 Portal Collection Bot Testing:</strong> Test portal bot credentials and view account availability. Credentials are not stored. Rate limit: 5 attempts per 5 minutes.
          </AlertDescription>
        </Alert>

        {/* KPI Cards */}
        <KPICards
          customers={accounts.length > 0 ? 1 : 0}
          suppliers={testCredentials?.supplierName ? 1 : 0}
          accounts={accounts.length}
          missingBills={accounts.filter(a => !a.latestBillUrl).length}
        />

        {/* Success Banner - Show when test is successful */}
        {accounts.length > 0 && testStatus === 'Success' && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6 shadow-sm">
            <div className="flex items-center gap-3 text-green-800">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎉</span>
                <span className="font-semibold text-lg">Congratulations!</span>
              </div>
            </div>
            <p className="text-green-700 mt-2 ml-6">You have successfully logged into your account using our plug-and-play portal bot. Below is the list of accounts associated with your profile:</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-6">
          <div className="order-2 lg:order-1">
            {isTesting ? (
              <ProgressAnimation
                percent={Math.floor(simulatedProgress)}
                stage={simulatedProgress < 15 ? 'connecting' : simulatedProgress < 35 ? 'authenticating' : simulatedProgress < 55 ? 'accessing_accounts' : simulatedProgress < 75 ? 'retrieving_bills' : 'parsing'}
                onCancel={handleCancelJob}
              />
            ) : (
              <AccountsResultsCard
                accounts={accounts}
                lastTested={lastTested}
                testStatus={testStatus}
                onDownloadLatest={handleDownloadLatest}
                on12MonthsClick={handle12MonthsClick}
                userId={testCredentials?.username || ''}
                portalName={testCredentials?.supplierName || ''}
                accountAssociated={accounts.length}
              />
            )}
          </div>

          <div className="order-1 lg:order-2">
            <TestConnectionForm
              onSubmit={handleTestConnection}
              isLoading={testPortalBot.isPending || isTesting || false}
              remainingAttempts={remainingAttempts}
              isRateLimited={!!rateLimitUntil && new Date() < rateLimitUntil}
              rateLimitMessage={rateLimitUntil && new Date() < rateLimitUntil 
                ? `Rate limit active. Please wait ${Math.ceil((rateLimitUntil.getTime() - new Date().getTime()) / (1000 * 60))} more minutes.`
                : undefined
              }
            />
          </div>
        </div>

        <SubscriptionModal
          open={showSubscriptionModal}
          onOpenChange={setShowSubscriptionModal}
        />
      </div>
    </ModuleAccessGuard>
  );
}
